import {
  useWSSnackbar,
  WSAlert,
  WSBreadcrumbsItem,
  WSElement,
  WSFlexBox,
  WSGrid,
  WSList,
  WSPage,
  WSPageToolbar,
  WSPanel,
  WSPill,
  WSPoster,
  WSSectionToolbar,
  WSText
} from "@wingspanhq/fe-component-library";
import {
  IPayerResponse,
  PayeeTaxDocumentSharePermission,
  PayerPayeeEngagementStatus,
  PayerPayeeStatus
} from "@wingspanhq/payments/dist/interfaces";
import { wsName } from "@wingspanhq/utils/dist/name/wsName";
import React, { useCallback, useState } from "react";
import { Route, Switch, useHistory, useRouteMatch } from "react-router-dom";
import { Tabs } from "../../../../components/Tabs";
import { useMemberPrivateFiles } from "../../../../query/files/queries";
import { useUserId } from "../../../../query/hooks/helpers";
import { useFeatureFlags } from "../../../../query/hooks/useFeatureFlags";
import { useIntegrationsQuickbooks } from "../../../../query/integrations/queries";
import { usePayerEngagementsListQuery } from "../../../../query/payerEngagements/queries/usePayerEngagementsListQuery";
import { useUpdatePayer } from "../../../../query/payers/mutations/useUpdatePayer";
import { usePayerQuery } from "../../../../query/payers/queries/usePayerQuery";
import { useRequirementsListQuery } from "../../../../query/requirements/queries/useRequirementsListQuery";
import { WSQueries } from "../../../../query/WSQuery";
import { useModalShareTaxInformationWithPayers } from "../../../../shared/components/ModalShareTaxInformationWithPayers/useModalShareTaxInformationWithPayers";
import { ResourceHistory } from "../../../../shared/components/ResourceHistory";
import { selectorIsQBOActive } from "../../../../shared/selectors/selectorIsQBOActive";
import { selectorIsEngagementsActiveForPayer } from "../../../Payees/selectors/selectorIsEngagementsActiveForPayer";
import { selectorPayerEngagementsTmp } from "../../../Payees/selectors/selectorPayerEngagementsTmp";
import { selectorPayerPayeeEngagements } from "../../../Payees/selectors/selectorPayerPayeeEngagements";
import { selectorExpiredRequirements } from "../../../Requirements/selectors/selectorExpiredRequirements";
import { selectorExpiringRequirements } from "../../../Requirements/selectors/selectorExpiringRequirements";
import { AutoPay } from "../../components/AutoPay";
import { ClientInfoPanel } from "../../components/ClientInfoPanel";
import { DocumentsSection } from "../../components/DocumentsSection";
import { DocumentsSectionType } from "../../components/DocumentsSection/types";
import { useEditClientModal } from "../../components/EditClientModal/useEditClientModal";
import { EngagementsTable } from "../../components/EngagementsTable";
import { PayerIntegrationWidget } from "../../components/IntegrationsSection/PayerIntegrationWidget";
import { usePayerHistoryResources } from "../../hooks/usePayerHistoryResources";
import {
  getPayerDetailsRequirementsUrl,
  getPayerDetailsUrl,
  getPayerHistoryUrl,
  PAYER_DETAILS_PATH,
  PAYER_DETAILS_REQUIREMENTS_PATH,
  PAYERS_PATH
} from "../../paths";
import {
  getAllEngagementsEligibilityRequirementsCompleted,
  getHasEligibleEngagements
} from "../../utils/engagement";
import { useArchiveClientModal } from "./ArchiveClientModal";
import { EligibilityRequirements } from "./EligibilityRequirements";
import { RecentInvoicesWidget } from "./RecentInvoicesWidget";
import { WireTransferSection } from "./WireTransferSection";

export const RouteClientDetails: React.FC = () => {
  const history = useHistory();

  const { openSnackbar } = useWSSnackbar();
  const match = useRouteMatch<{ payerId: string }>();
  const userId = useUserId();
  const payerId = match.params.payerId;
  const modalFormW9PayerConsent = useModalShareTaxInformationWithPayers();

  const [showArchived, setShowArchived] = useState<boolean>(false);

  const queryClient = usePayerQuery(payerId);
  const queryPayerEngagements = usePayerEngagementsListQuery(payerId);

  const queryRequirements = useRequirementsListQuery({
    filter: {
      payerId,
      payeeId: userId,
      payerPayeeEngagementIds: {
        "!=": "null"
      }
    },
    sort: {
      createdAt: "asc"
    },
    page: {
      size: 99
    }
  });

  const qDocuments = useMemberPrivateFiles({
    filter: {
      hidden: true,
      tags: { in: [`payer:${payerId}`] }
    }
  });
  const [restoreClient] = useUpdatePayer();

  const archiveClientModal = useArchiveClientModal();
  const editClientModal = useEditClientModal();

  const queryFeatureFlags = useFeatureFlags();
  const queryQBO = useIntegrationsQuickbooks();

  const handleCreateInvoice = useCallback(() => {
    const state =
      queryClient.data && queryPayerEngagements.data?.length === 1
        ? {
            payerId: queryClient.data.payerId,
            payerPayeeEngagementId:
              queryPayerEngagements.data[0].payerPayeeEngagementId
          }
        : {
            email: queryClient?.data?.user?.email
          };

    history.push("/member/invoices/create", state);
  }, [queryClient.data, queryPayerEngagements.data]);

  const handleEditClient = () => {
    editClientModal.open({
      client: queryClient?.data as IPayerResponse
    });
  };

  const handleRestoreClient = async (client: IPayerResponse) => {
    await restoreClient(
      {
        id: client.payerId,
        payeeOwnedData: {
          status: PayerPayeeStatus.Active
        }
      },
      {
        onSuccess: () => {
          openSnackbar({
            duration: 5000,
            type: "success",
            message: "Client restored successfully"
          });
        },
        onError: () => {
          openSnackbar({
            duration: 5000,
            type: "warning",
            message: `Failed to restore client`
          });
        }
      }
    );
  };

  const historyResources = usePayerHistoryResources(payerId);

  return (
    <WSPage>
      <WSQueries
        queries={{
          queryClient,
          queryPayerEngagements,
          queryFeatureFlags,
          queryQBO,
          queryRequirements
        }}
      >
        {({
          queryClientData: client,
          queryPayerEngagementsData: payerEngagements,
          queryQBOData,
          queryFeatureFlagsData,
          queryRequirementsData
        }) => {
          const clientInfo = wsName({
            user: {
              email: client.user?.email as string,
              profile: client.user?.profile
            },
            member: client.member,
            payeeOwnedData: client.payeeOwnedData
          });
          const activeEngagements = payerEngagements.filter(
            e => e.status !== PayerPayeeEngagementStatus.Inactive
          );
          const areRequirementsCompleted =
            getAllEngagementsEligibilityRequirementsCompleted(
              activeEngagements
            );

          const hasEligibleEngagements =
            getHasEligibleEngagements(activeEngagements);

          const isInactive =
            client.payeeOwnedData.status === PayerPayeeStatus.Inactive;
          const isNotShared =
            client.payeeOwnedData?.shareTaxDocument !==
            PayeeTaxDocumentSharePermission.Allow;

          const isQuickbooksActive = selectorIsQBOActive(
            queryFeatureFlagsData,
            queryQBOData
          );

          const forceShowEngagements =
            isQuickbooksActive ||
            payerEngagements.some(
              eng => eng.status === PayerPayeeEngagementStatus.Inactive
            );

          const filteredEngagements = selectorIsEngagementsActiveForPayer(
            payerEngagements
          )
            ? selectorPayerPayeeEngagements(payerEngagements, false, true)
            : selectorPayerEngagementsTmp(
                payerEngagements,
                forceShowEngagements
              );

          const activeFilteredEngagements = filteredEngagements.filter(
            eng => eng.status === PayerPayeeEngagementStatus.Active
          );
          const inactiveFilteredEngagements = filteredEngagements.filter(
            eng => eng.status === PayerPayeeEngagementStatus.Inactive
          );

          const expiringRequirements = selectorExpiringRequirements(
            queryRequirementsData || []
          );

          const expiredRequirements = selectorExpiredRequirements(
            queryRequirementsData || []
          );

          return (
            <>
              <WSPageToolbar
                breadcrumbs={[
                  {
                    label: "Clients",
                    onClick: () => {
                      history.push(PAYERS_PATH);
                    }
                  },
                  ...(isInactive
                    ? [
                        {
                          label: "Archived clients",
                          onClick: () => {
                            history.push(`${PAYERS_PATH}/archived`);
                          }
                        } as WSBreadcrumbsItem
                      ]
                    : []),
                  {
                    label: clientInfo.getResolvedName()
                  }
                ]}
                actions={
                  isInactive
                    ? [
                        {
                          label: "Restore client",
                          icon: "repeat-circle",
                          onClick: () => {
                            handleRestoreClient(client);
                          }
                        }
                      ]
                    : [
                        {
                          icon: "plus",
                          label: "Create invoice",
                          buttonKind: "Primary",
                          onClick: handleCreateInvoice
                        },
                        {
                          icon: "edit",
                          label: "Edit client",
                          onClick: handleEditClient
                        },
                        {
                          icon: "archive",
                          label: "Archive client",
                          onClick: () => {
                            archiveClientModal.open({
                              client
                            });
                          }
                        }
                        // FIX: implement in future
                        // {
                        //   icon: "trash",
                        //   iconProps: {
                        //     color: "red400"
                        //   },
                        //   label: "Remove client",
                        //   labelProps: {
                        //     color: "red400"
                        //   },
                        //   onClick: () => {
                        //     removeClientModal.open({
                        //       client
                        //     });
                        //   }
                        // }
                      ]
                }
              />
              <WSFlexBox.CenterY mb="XL">
                <WSText.Heading3 mr="L">
                  {clientInfo.getResolvedName()}
                </WSText.Heading3>
                {client.payeeOwnedData.status === PayerPayeeStatus.Inactive ? (
                  <WSPill theme="neutral" text="Archived" />
                ) : null}
              </WSFlexBox.CenterY>
              <WSList gap="L">
                {
                  // show if registered but not shared
                  isNotShared ? (
                    <WSAlert
                      icon="alert"
                      size="M"
                      theme="warning"
                      title="Tax information not shared"
                      action={{
                        onClick: () => {
                          modalFormW9PayerConsent.open({
                            payerId: client.payerId
                          });
                        },
                        text: "Share tax information"
                      }}
                    >
                      <WSText.ParagraphSm weight="book" color="gray700">
                        You have not consented to share your tax information
                        with the client. Future payments to you may not be
                        completed until the information is shared.
                      </WSText.ParagraphSm>
                    </WSAlert>
                  ) : areRequirementsCompleted && !hasEligibleEngagements ? (
                    <WSAlert
                      theme="warning"
                      icon="alert"
                      title="Payments eligibility pending"
                    >
                      You need to have at least one active engagement that is
                      eligible for payments to receive payments from this
                      client.
                    </WSAlert>
                  ) : !areRequirementsCompleted ? (
                    <WSAlert
                      theme="warning"
                      icon="alert"
                      title="Engagement requirements incomplete"
                    >
                      You will not be able to receive payouts for an engagement
                      until all requirements for that engagement is complete.
                    </WSAlert>
                  ) : expiringRequirements.length > 0 ||
                    expiredRequirements.length > 0 ? (
                    <>
                      {expiredRequirements.length > 0 && (
                        <WSAlert
                          theme="warning"
                          icon="alert"
                          title="Requirements incomplete"
                          action={{
                            text: "Renew now",
                            onClick: () => {
                              history.push(
                                getPayerDetailsRequirementsUrl(payerId)
                              );
                            }
                          }}
                        >
                          You must complete all eligibility requirements for an
                          engagement before you can receive payments for work
                          under that engagement.
                        </WSAlert>
                      )}

                      {expiringRequirements.length > 0 && (
                        <WSAlert
                          theme="neutral"
                          icon="calendar"
                          title="Requirements expiring soon"
                          action={{
                            text: "View requirements",
                            onClick: () => {
                              history.push(
                                getPayerDetailsRequirementsUrl(payerId)
                              );
                            }
                          }}
                        >
                          You have {expiringRequirements.length} requirements
                          expiring soon.
                        </WSAlert>
                      )}
                    </>
                  ) : null
                }

                <WSGrid gutter="2XL">
                  <WSGrid.Item span={{ m: "8" }}>
                    <Tabs
                      mb="L"
                      tabs={[
                        {
                          label: "Summary",
                          path: getPayerDetailsUrl(payerId)
                        },
                        {
                          label: "Requirements",
                          path: getPayerDetailsRequirementsUrl(payerId),
                          pill: {
                            icon: true,
                            theme:
                              !isNotShared && areRequirementsCompleted
                                ? "success"
                                : "warning"
                          }
                        }
                      ]}
                    />
                    <Switch>
                      <Route
                        path={PAYER_DETAILS_PATH}
                        exact
                        render={() => (
                          <WSList gap="L">
                            <WSElement>
                              <WSSectionToolbar title="Client information" />
                              <ClientInfoPanel client={client} />
                            </WSElement>
                            <WSElement>
                              <WSSectionToolbar title="Engagements" mb="M" />
                              {activeFilteredEngagements.length ? (
                                <EngagementsTable
                                  engagements={activeFilteredEngagements}
                                />
                              ) : (
                                <WSPanel>
                                  <WSPoster
                                    size="S"
                                    type="tasks"
                                    title="No engagements active"
                                    description="Once an engagement is assigned and active, it will appear here."
                                  />
                                </WSPanel>
                              )}
                            </WSElement>
                            {inactiveFilteredEngagements.length ? (
                              <WSElement>
                                <WSSectionToolbar
                                  title={
                                    showArchived ? "Archived engagements" : ""
                                  }
                                  mb="M"
                                  buttons={[
                                    {
                                      label: showArchived
                                        ? `Hide archived (${inactiveFilteredEngagements.length})`
                                        : `Show archived (${inactiveFilteredEngagements.length})`,
                                      onClick: () => {
                                        setShowArchived(v => !v);
                                      }
                                    }
                                  ]}
                                />
                                {showArchived ? (
                                  <EngagementsTable
                                    engagements={inactiveFilteredEngagements}
                                  />
                                ) : null}
                              </WSElement>
                            ) : null}
                          </WSList>
                        )}
                      />
                      <Route
                        path={PAYER_DETAILS_REQUIREMENTS_PATH}
                        render={() => (
                          <EligibilityRequirements
                            payer={client}
                            hasEligibleEngagements={hasEligibleEngagements}
                            requirements={queryRequirementsData}
                            noActions={false}
                          />
                        )}
                      />
                    </Switch>
                  </WSGrid.Item>

                  <WSGrid.Item span={{ m: "4" }}>
                    <WSList>
                      <RecentInvoicesWidget payerId={client.payerId} />
                      {/* Wire transfer section */}
                      <WireTransferSection client={client} />

                      {/* Documents section */}
                      {qDocuments?.data && qDocuments.data.length > 0 ? (
                        <DocumentsSection
                          isInactive={isInactive}
                          type={DocumentsSectionType.Client}
                          payerId={payerId}
                          payeeId={userId}
                        />
                      ) : null}

                      {/* Integration mappings section */}

                      <PayerIntegrationWidget
                        isInactive={isInactive}
                        payerId={client.payerId}
                        customerId={
                          client?.payeeOwnedData?.integration?.quickbooks
                            ?.customerId
                        }
                        itemId={
                          client?.payeeOwnedData?.integration?.quickbooks
                            ?.itemId
                        }
                      />

                      {/* Autopay section */}
                      <AutoPay client={client} />

                      <ResourceHistory
                        resources={historyResources}
                        onViewAll={() => {
                          history.push(getPayerHistoryUrl(payerId));
                        }}
                      />
                    </WSList>
                  </WSGrid.Item>
                </WSGrid>
              </WSList>
            </>
          );
        }}
      </WSQueries>
    </WSPage>
  );
};
